import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { Certificate } from 'app/library/certificate/models/certificate.model';
import { CertificateService } from 'app/library/certificate/services/certificate.service';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { EditManagerPermissionsDialogComponent } from 'app/library/module-managers/components/dialogs/edit-manager-permissions-dialog/edit-manager-permissions-dialog.component';
import { ModuleManagersService } from 'app/library/module-managers/services/module-managers.service';
import {
    AddUsersToModulesAsManagers,
    AddUsersToModulesAsManagersSuccess,
    AddUsersToModulesAsParticipants,
    AddUsersToModulesAsParticipantsSuccess,
    RemoveUserAsResponsibleSuccess,
    RemoveUsersFromModulesAsManagers,
    RemoveUsersFromModulesAsManagersSuccess,
    UpdateModuleUserResponsibleFailure,
} from 'app/library/module-user/actions';
import { ModuleUserExtended } from 'app/library/module-user/models/module-user.extended.model';
import { ModuleUser } from 'app/library/module-user/models/module-user.model';
import { ModuleKind } from 'app/library/module/enums/module-kind.enum';
import { ModuleCategory } from 'app/library/module/enums/moduleCategory';
import { ModuleStatuses } from 'app/library/module/models/module-statuses';
import { Module } from 'app/library/module/models/module.model';
import { ModuleGlobalService } from 'app/library/module/services/module-global.service';
import { SortMyPinnedModules } from 'app/library/my-profile-pinned-modules/actions';
import { MaxBrainTag } from 'app/library/tag/models/tag.model';
import { TagMultiSelectService } from 'app/library/tag/services/multi-select.service';
import { TeamCourseMemberDetailsDialogComponent } from 'app/main/pages/modules/module-form/module-form-participants-tab/team-course-member-details-dialog/team-course-member-details-dialog.component';
import { TeamProgramMemberDetailsDialogComponent } from 'app/main/pages/modules/module-form/module-form-participants-tab/team-program-member-details-dialog/team-program-member-details-dialog.component';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { NameDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/name/name.component';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { RemoveUserAsResponsible, UpdateModuleUserResponsible, UpdateModuleUserResponsibleSuccess } from '../../../module-user/actions/index';
import { UserModuleRemoveParticipantService } from '../../services/user-module-remove-participant.service';
import { PinnedModuleDataSource } from './pinned-module-datasource';

@Component({
    selector: 'app-my-pinned-modules-list-table',
    templateUrl: './my-pinned-modules-list-table.component.html',
    styleUrls: ['./my-pinned-modules-list-table.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class MyPinnedModulesListTableComponent extends UnsubscribeOnDestroy implements OnInit, AfterViewInit {
    @Input()
    myPinnedModuleUser$: Observable<ModuleUserExtended[]>;

    @Input()
    searchTerm: string;

    @Input()
    displayedColumns: string[];

    @Input()
    isManagerPage: boolean;

    @Input()
    currentUserId: string;

    @Input()
    isTeamMembersPage?: boolean;

    @Input()
    enableModuleRedirect?: boolean = true;

    @Input()
    canDownloadCertificates: boolean = true;

    @Output()
    clickedTag = new EventEmitter<MaxBrainTag>();

    @ViewChild(MatSort)
    sort: MatSort;

    moduleStatuses = ModuleStatuses;
    profileId = this._store.selectSnapshot(UserState).me;
    dataSource: PinnedModuleDataSource;
    ModuleKind = ModuleKind;
    /**
     * Bumps after layout/resize so filterTags remeasures with a real cell width.
     * (Pure pipe would otherwise keep a stale empty/partial result.)
     */
    tagsLayoutKey = 0;
    private readonly _resize$ = new Subject<void>();

    get hasTenantAdminOrUsersViewPermission(): boolean {
        return this.profile.permissions.includes(AdminPermissions.USERS_READ) || this.profile.permissions.includes(AdminPermissions.TENANT_MANAGEMENT);
    }

    profile = this._store.selectSnapshot(UserState.getMyInfo);

    @HostListener('window:resize')
    onWindowResize(): void {
        this._resize$.next();
    }

    constructor(
        public tagMultiSelectService: TagMultiSelectService,
        private _action$: Actions,
        private _store: Store,
        private moduleGlobalService: ModuleGlobalService,
        private _moduleManagersService: ModuleManagersService,
        private _certificateService: CertificateService,
        private _cd: ChangeDetectorRef,
        private _matDialog: MatDialog,
        private _translateService: TranslateService,
        private _matSnackBar: MatSnackBar,
        private _userModuleRemoveParticipantService: UserModuleRemoveParticipantService
    ) {
        super();
        this._resize$.pipe(debounceTime(150), takeUntil(this._unsubscribeAll)).subscribe(() => this._bumpTagsLayout());
    }

    ngOnInit(): void {
        this.dataSource = new PinnedModuleDataSource(this.myPinnedModuleUser$, this._store, this._action$, this.tagMultiSelectService);

        setTimeout(() => {
            this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
                this._store.dispatch(new SortMyPinnedModules(sort));

                this.loadPinnedModulesPage();
            });
        });
    }

    ngAfterViewInit(): void {
        this._cd.detectChanges();
        setTimeout(() => {
            requestAnimationFrame(() => this._bumpTagsLayout());
        });
    }

    private _bumpTagsLayout(): void {
        this.tagsLayoutKey++;
        this._cd.detectChanges();
    }

    filterByTag(tag: MaxBrainTag): void {
        this.clickedTag.emit(tag);
    }

    loadPinnedModulesPage(): void {
        this.dataSource.loadPinnedModules(
            ModuleCategory.Pinned,
            this.searchTerm,
            this.sort.active,
            this.sort.direction,
            100,
            1,
            this.currentUserId,
            this.tagMultiSelectService.selectedTags.map((tag) => parseInt(tag.id)),
            this.isManagerPage ? false : true,
            this.isManagerPage ? true : false
        );
    }

    goToModule(module: Module): void {
        // On Team Members page we want to show user state on the module
        if (this.isTeamMembersPage) {
            if (module.kind === ModuleKind.Normal) {
                this.openTeamCourseMemberDetailsDialog(module);
            } else {
                this.openTeamProgramMemberDetailsDialog(module);
            }
        } else {
            this.moduleGlobalService.setParentUrl(window.location.pathname);
            this._moduleManagersService.goToModule(this.profileId, module, 'overview');
        }
    }

    openTeamProgramMemberDetailsDialog(currentModule: Module): void {
        const dialog = this._matDialog.open(TeamProgramMemberDetailsDialogComponent, {
            disableClose: false,
            width: '1125px',
            data: {
                moduleId: currentModule.id,
                title: currentModule.name,
                participantId: this.currentUserId,
            },
        });

        dialog.componentInstance.currentModule = currentModule;
    }

    openTeamCourseMemberDetailsDialog(currentModule: Module): void {
        const dialogRef = this._matDialog.open(TeamCourseMemberDetailsDialogComponent, {
            disableClose: false,
            width: '1125px',
        });

        dialogRef.componentInstance.moduleId = currentModule.id;
        dialogRef.componentInstance.title = currentModule.name;
        dialogRef.componentInstance.participantId = this.currentUserId;
    }

    addAsParticipant(moduleUser: ModuleUser): void {
        this._store.dispatch(
            new AddUsersToModulesAsParticipants({
                userIds: [moduleUser.userId],
                moduleIds: [moduleUser.moduleId],
            })
        );

        this._action$.pipe(ofActionDispatched(AddUsersToModulesAsParticipantsSuccess), takeUntil(this._unsubscribeAll)).subscribe((action) => {
            const successMessage = this._translateService.instant('GENERAL.MESSAGE.SAVED');

            // Show the success message
            this._matSnackBar.open(successMessage, 'OK', {
                verticalPosition: 'top',
                duration: SnackBarTime.medium,
            });
        });
    }

    removeAsParticipant(moduleUser: ModuleUserExtended): void {
        this._userModuleRemoveParticipantService.removeAsParticipant(moduleUser, this._unsubscribeAll, () => this.loadPinnedModulesPage());
    }

    addAsManager(moduleUser: ModuleUser): void {
        const dialogRef = this._matDialog.open(EditManagerPermissionsDialogComponent, {
            disableClose: false,
            width: '632px',
            autoFocus: false,
        });

        dialogRef.componentInstance.title = this._translateService.instant('LIST.USER_MODULES.CHOOSE_PERMISSIONS');

        dialogRef.afterClosed().subscribe(
            (result: {
                data: {
                    permissions: string[];
                };
            }) => {
                if (result && result.data) {
                    this._store.dispatch(
                        new AddUsersToModulesAsManagers({
                            userIds: [moduleUser.userId],
                            moduleIds: [moduleUser.moduleId],
                            permissions: result.data.permissions,
                        })
                    );

                    this._action$.pipe(ofActionDispatched(AddUsersToModulesAsManagersSuccess), takeUntil(this._unsubscribeAll)).subscribe((action) => {
                        const successMessage = this._translateService.instant('GENERAL.MESSAGE.SAVED');

                        this._matSnackBar.open(successMessage, 'OK', {
                            verticalPosition: 'top',
                            duration: SnackBarTime.medium,
                        });
                    });
                }
            }
        );
    }

    removeAsManager(moduleUser: ModuleUser): void {
        const dialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
        });

        dialogRef.componentInstance.confirmTitle = 'FORM.ASSIGN_TO_MODULES.FIELD.REMOVE_AS_MANAGER';
        dialogRef.componentInstance.confirmButtonColor = 'warn';
        dialogRef.componentInstance.confirmMessage = 'GENERAL.MESSAGE.REMOVE_CONFIRMATION';
        dialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.REMOVE';

        dialogRef.afterClosed().subscribe((result: boolean) => {
            if (result) {
                this._store.dispatch(
                    new RemoveUsersFromModulesAsManagers({
                        userIds: [moduleUser.userId],
                        moduleIds: [moduleUser.moduleId],
                    })
                );
            }
        });

        this._action$.pipe(ofActionDispatched(RemoveUsersFromModulesAsManagersSuccess), takeUntil(this._unsubscribeAll)).subscribe((action) => {
            dialogRef.close();
            const successMessage = this._translateService.instant('GENERAL.MESSAGE.SAVED');

            // Show the success message
            this._matSnackBar.open(successMessage, 'OK', {
                verticalPosition: 'top',
                duration: SnackBarTime.medium,
            });
        });
    }

    setAsResponsible(moduleUser: ModuleUser): void {
        const dialogRef = this._matDialog.open(NameDialogComponent, {
            disableClose: false,
            width: '482px',
        });

        dialogRef.componentInstance.dialogTitle = 'FORM.ASSIGN_TO_MODULES.FIELD.ADD_AS_RESPONSIBLE';
        dialogRef.componentInstance.placeholder = 'GENERAL.PROPERTY.ROLE';
        dialogRef.componentInstance.name = moduleUser.roleLabel || '';

        dialogRef.componentInstance.save.subscribe((name: string) => {
            this._store.dispatch(
                new UpdateModuleUserResponsible({
                    userIds: [moduleUser.userId],
                    moduleIds: [moduleUser.moduleId],
                    roleLabel: name,
                })
            );

            const successSubscription = this._action$.pipe(ofActionDispatched(UpdateModuleUserResponsibleSuccess), takeUntil(this._unsubscribeAll)).subscribe((action) => {
                dialogRef.close();
                const successMessage = this._translateService.instant('GENERAL.MESSAGE.SAVED');

                // Show the success message
                this._matSnackBar.open(successMessage, 'OK', {
                    verticalPosition: 'top',
                    duration: SnackBarTime.medium,
                });

                failureSubscription.unsubscribe();
            });

            const failureSubscription = this._action$
                .pipe(ofActionDispatched(UpdateModuleUserResponsibleFailure), takeUntil(this._unsubscribeAll))
                .subscribe((action: UpdateModuleUserResponsibleFailure) => {
                    dialogRef.componentInstance.errors.next(action.payload.response.error);
                    successSubscription.unsubscribe();
                });
        });
    }

    removeAsResponsible(moduleUser: ModuleUser): void {
        const dialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
        });

        dialogRef.componentInstance.confirmTitle = 'FORM.ASSIGN_TO_MODULES.FIELD.REMOVE_AS_RESPONSIBLE';
        dialogRef.componentInstance.confirmButtonColor = 'warn';
        dialogRef.componentInstance.confirmMessage = 'GENERAL.MESSAGE.REMOVE_CONFIRMATION';
        dialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.REMOVE';

        dialogRef.afterClosed().subscribe((result: boolean) => {
            if (result) {
                this._store.dispatch(
                    new RemoveUserAsResponsible({
                        userIds: [moduleUser.userId],
                        moduleIds: [moduleUser.moduleId],
                    })
                );

                this._action$.pipe(ofActionDispatched(RemoveUserAsResponsibleSuccess), takeUntil(this._unsubscribeAll)).subscribe(() => {
                    this.loadPinnedModulesPage();
                });
            }
        });
    }

    downloadCertificate(certificate: Certificate): void {
        this._certificateService.downloadFinalGradeCertificate(certificate).then(() => {});
    }

    visualLength(text: string): number {
        const ruler = document.getElementById('ruler-2');
        ruler.innerHTML = text;
        const toReturn = ruler.offsetWidth;
        ruler.innerHTML = '';
        return toReturn;
    }
}
